import type {FC} from 'react';
import React from 'react'
import useEvent from '../hooks/useEvent'
import {appendPopup} from '../helpers/react-helpers'
import type { ImageCropPopupProps, RatioCrop} from './ImageCropPopup';
import {ImageCropPopup} from './ImageCropPopup'
import {notifyError, notifySuccess, notifyWarning} from '../notify'
import {captureSentryError} from '../sentry'
import css from './CreateCompanyAliasPage.less'
import {resolveRoute, uploadBlob} from '../helpers/ajax'
import icons from '../../markons/markons'
import type {JsonResponse} from '../helpers/jsonSerializable'
import {useUpdateState} from '../hooks/useUpdateState'
import {
    humanFileSize,
    formatPercentShort
} from '../kb'
import Icon from './Icon'
import cn from 'classnames'
import ActionLink from './ActionLink'
import {
    applyFindAndReplace,
    arrayCount,
    findAndReplace,
    mapDefined
} from '../util/collections'

/** @deprecated */
export const JPEG = 'image/jpeg'
/** @deprecated */
export const PNG = 'image/png'
/** @deprecated */
const SVG = 'image/svg+xml'
/** @deprecated */
const WEBP = 'image/webp'

const enum ImageType {
    JPEG = 'image/jpeg',
    PNG = 'image/png',
    SVG = 'image/svg+xml',
    WEBP = 'image/webp',
}

/** @deprecated */
export const inputToOutputType = Object.freeze<Record<string, string>>({
    [PNG]: PNG,
    [WEBP]: PNG,
    [SVG]: PNG,
})

const SUPPORTS_TRANPARENCY = Object.freeze(new Set<string>([ImageType.PNG, ImageType.WEBP, ImageType.SVG]))

interface CropOptions {
    maxWidth: number
    maxHeight: number
    crop: RatioCrop,
    image: HTMLImageElement
    file: File
    quality?: number
}

interface ProgressEvent {
    loaded: number
    total: number
    progress: number
}

type UploadError = {
    status?: number
    code: string
}

interface UploadOptions<TRes> {
    blob: Blob
    url: string
    onProgress?: (ev: ProgressEvent) => void,
    onComplete?: (ev: { data: TRes, status: number }) => void,
    onError?: (ev: UploadError) => void,
    meta: {
        width: number
        height: number
        filename: string
        /** Last time the file was modified, in ms since epoch */
        lastModified: number
    }
}

function startUpload<TRes = JsonResponse>(opts: UploadOptions<TRes>) {
    return new Promise<TRes>((resolve, reject) => {
        opts.onProgress?.({
            loaded: 0,
            total: opts.blob.size,
            progress: 0,
        })

        const xhr = uploadBlob(opts.url, opts.blob, {
            'X-Width': opts.meta.width,
            'X-Height': opts.meta.height,
            'X-Filename': opts.meta.filename,
            'X-Last-Modified-Ms': opts.meta.lastModified,
        })

        xhr.upload.addEventListener('progress', ev => {
            if(!ev.lengthComputable) return
            opts.onProgress?.({
                loaded: ev.loaded,
                total: ev.total,
                progress: ev.loaded / ev.total,
            })
        })

        xhr.addEventListener('loadend', () => {
            // opts.onProgress?.({
            //     loaded: opts.blob.size,
            //     total: opts.blob.size,
            //     progress: 1,
            // })

            if(xhr.readyState !== 4) {
                // logErrorMessage(`XHR fired "loadend" but readyState was ${xhr.readyState}`)
                opts.onError?.({
                    status: xhr.status,
                    code: 'unexpected_ready_state',
                })
                return reject("Upload did not complete")
            }

            if(xhr.status >= 200 && xhr.status < 300) {
                try {
                    const res = JSON.parse(xhr.responseText)
                    opts.onComplete?.({
                        status: xhr.status,
                        data: res,
                    })
                    // setUploadState({logoImageUrl: res.url})
                    return resolve(res)
                } catch(err) {
                    captureSentryError("ImageUploadForm > startUpload", err, {
                        status: xhr.status,
                        function: 'ImageUploadForm_loadend',
                    }, '1bad7b52-f425-4cba-9118-a51858de1566')
                    opts.onError?.({
                        status: xhr.status,
                        code: 'parse_error',
                    })
                    return reject("Upload succeeded but response could not be parsed")
                }
            }

            // logErrorMessage(`Upload returned ${xhr.status}`)
            opts.onError?.({
                status: xhr.status,
                code: 'error_status',
            })
            return reject("Upload failed")
        })

        xhr.addEventListener('error', () => {
            captureSentryError(`Upload error`,null,null,'a28b6d07-36f6-4cbf-a1e0-198c1da89f91')
            opts.onError?.({
                status: xhr.status,
                code: 'network_error',
            })
            return reject("Upload error")
        })

        xhr.addEventListener('abort', () => {
            // logWarningMessage("Upload aborted")
            opts.onError?.({
                status: xhr.status,
                code: 'aborted',
            })
            return reject("Upload aborted")
        })
    })
}

interface CropImageResult {
    blob: Blob,
    width: number
    height: number
    // contentType: string
}

const MAX_PNG_SIZE = (1024 ** 2) * 5

function cropImage({crop, image, file, maxWidth, maxHeight, quality = 0.9}: CropOptions) {
    return new Promise<CropImageResult>((resolve, reject) => {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')

        if(!ctx) {
            captureSentryError("Could not get 2d context",null,null,'90a3ca50-66de-49e4-b6cf-ff56613ffb58')
            return reject("Could not crop image; please try a newer browser")
        }

        const cropW = image.naturalWidth * crop.width
        const cropH = image.naturalHeight * crop.height
        const cropX = image.naturalWidth * crop.x
        const cropY = image.naturalHeight * crop.y

        // TODO: factor in output file size instead of input size?
        const destType = SUPPORTS_TRANPARENCY.has(file.type) && file.size < MAX_PNG_SIZE ? ImageType.PNG : ImageType.JPEG

        let scale = 1
        if(cropW > maxWidth || cropH > maxHeight) {
            scale = Math.min(maxWidth / cropW, maxHeight / cropH)
        }

        canvas.width = Math.round(cropW * scale)
        canvas.height = Math.round(cropH * scale)

        if(!SUPPORTS_TRANPARENCY.has(destType)) {
            // If output format does not support transparency, fill with white to avoid black background.
            ctx.fillStyle = 'white'
            ctx.fillRect(0, 0, canvas.width, canvas.height)
        }

        ctx.imageSmoothingQuality = 'high'
        ctx.drawImage(image, cropX, cropY, cropW, cropH, 0, 0, canvas.width, canvas.height)

        canvas.toBlob(blob => {
            if(!blob) {
                captureSentryError("Could not convert canvas to blob",null,null,'fc391207-62da-4952-938e-6fd6b7e30b97')
                return reject("Could not save image")
            }

            resolve({
                blob,
                width: canvas.width,
                height: canvas.height,
                // contentType: destType,
            })
        }, destType, quality)
    })
}

export type UploadStatusUpdateEvent = { activeUploads: number }
export type UploadStatusUpdateEventHandler = EventCallback<UploadStatusUpdateEvent>
export type UploadChangeEvent = { imageIds: number[] }
export type UploadChangeEventHandler = EventCallback<UploadChangeEvent>


interface ImageUploadProps extends Pick<ImageCropPopupProps, 'initialAspectRatio' | 'forceAspectRatio'> {
    onChange: UploadChangeEventHandler
    onStatusUpdate: EventCallback<{ activeUploads: number }>
    images?: ImageUpload[]
}

type UploadStateInfo = {
    uploading: boolean
    /** 0-1 */
    uploadProgress: number
    logoImageUrl: string | null
}

export type ImageUpload = {
    id: number
    url: string
    width: number
    height: number
    size: number
    filename: string
}

type Image = Override<ImageUpload, {
    id: number | null
    key: string
    uploadProgress?: number | null
    uploading: boolean,
}>

type ImageUploadFormState = {
    images: Image[]
}

type UploadResponse = {
    id: number
    url: string
}

export const ImageUploadForm: FC<ImageUploadProps> = ({
    images,
    initialAspectRatio,
    forceAspectRatio,
    onStatusUpdate,
    onChange
}) => {

    const [state, updateState] = useUpdateState<ImageUploadFormState>(() => ({
        images: images?.length ? images.map(img => ({
                ...img,
                key: kb.id(),
                uploadProgress: 1,
                uploading: false,
            }
        )) : [],
    }))


    // TODO: allow deleting images
    // TODO: allow uploading more than 1 image at a time (show one crop popup after the other with "(1 of N)" in the
    // title)

    const onSelectFile = useEvent((ev: React.ChangeEvent<HTMLInputElement>) => {
        if(!ev.target.files?.length) return
        const file = ev.target.files[0]!
        const url = URL.createObjectURL(file)
        ev.target.value = ''
        appendPopup(unmount => {
            const closePopup = () => {
                unmount()
                URL.revokeObjectURL(url)
            }
            return (
                <ImageCropPopup
                    {...{initialAspectRatio, forceAspectRatio}}
                    onSave={(crop, image) => {
                        cropImage({crop, image, file, maxWidth: 2000, maxHeight: 2000, quality: 0.8})
                            .then(({blob, width, height}) => {
                                const key = kb.id()
                                const url = URL.createObjectURL(blob)

                                updateState({
                                    images: {
                                        $apply: (images: Image[]) => {
                                            images = [...images, {
                                                id: null,
                                                url,
                                                width,
                                                height,
                                                filename: file.name,
                                                size: blob.size,
                                                key,
                                                uploadProgress: 0,
                                                uploading: true,
                                            }]

                                            onStatusUpdate?.({
                                                // TODO: seems like this would be easier with a reducer/selector
                                                activeUploads: arrayCount(images, img => img.uploading)
                                            })

                                            return images
                                        }
                                    }
                                })

                                startUpload<UploadResponse>({
                                    blob,
                                    url: resolveRoute('companies.logoUpload'),
                                    meta: {
                                        filename: file.name,
                                        lastModified: file.lastModified,
                                        width,
                                        height,
                                    },
                                    onProgress: ev => {
                                        updateState({
                                            images: {
                                                $apply: applyFindAndReplace(img => img.key === key, img => ({
                                                    ...img,
                                                    uploadProgress: ev.progress,
                                                })),
                                            }
                                        })
                                    },
                                    onError: ev => {
                                        notifyError("Upload failed")
                                        URL.revokeObjectURL(url)
                                        updateState({
                                            images: {
                                                $apply: (images: Image[]) => {
                                                    images = findAndReplace(images, img => img.key === key, img => ({
                                                        ...img,
                                                        // url: null,
                                                        uploading: false,
                                                    }))

                                                    onStatusUpdate?.({
                                                        activeUploads: arrayCount(images, img => img.uploading)
                                                    })

                                                    return images
                                                },
                                            }
                                        })
                                    },
                                    onComplete: ev => {
                                        notifySuccess(`${file.name} uploaded`)
                                        URL.revokeObjectURL(url)
                                        updateState({
                                            images: {
                                                $apply: (images: Image[]) => {
                                                    images = findAndReplace(images, img => img.key === key, img => ({
                                                        ...img,
                                                        id: ev.data.id,
                                                        url: ev.data.url,
                                                        uploadProgress: 1,
                                                        uploading: false,
                                                    }))

                                                    onStatusUpdate?.({
                                                        activeUploads: arrayCount(images, img => img.uploading)
                                                    })
                                                    onChange?.({
                                                        imageIds: mapDefined(images, img => img.id)
                                                    })

                                                    return images
                                                },
                                            }
                                        })
                                    },
                                }).catch(console.error)
                            }, err => {
                                notifyError(err)
                            })

                        closePopup()
                    }}
                    src={url}
                    onCancel={() => {
                        notifyWarning("Upload cancelled")
                        closePopup()
                    }}
                />
            )
        })
    })

    const handleDelete = (key: string) => () => {
        updateState({
            images: {
                $apply: (images: Image[]) => {
                    images = images.filter(img => img.key !== key)

                    onChange?.({
                        imageIds: mapDefined(images, img => img.id)
                    })

                    return images
                },
            }
        })
    }

    return (
        <>
            {state.images?.length ? <div className={css.imageRow}>
                {state.images.map(img => (
                    <div key={img.key} className={css.imageWrap}>
                        <img className={css.uploadedImage}
                            alt={img.filename}
                            src={img.url}
                            width={img.width}
                            height={img.height} />
                        {img.id ? <ActionLink onClick={handleDelete(img.key)}
                            className={cn(css.deleteWrapper, "delete-link")}>
                            <Icon name={icons.ionTrashA} />
                        </ActionLink> : null}
                        <div className={css.fileInfo}>
                            {img.filename ? <div>{img.filename}</div> : null}
                            <div>{humanFileSize(img.size)} | {img.width} &times; {img.height} px</div>
                            {img.uploading && img.uploadProgress != null ?
                                <div>{formatPercentShort(img.uploadProgress)}</div> : null}
                        </div>
                    </div>
                ))}
            </div> : null}
            <div>
                <input className={state.images?.length ? css.fileUploadInline : css.fileUploadBlock} type="file"
                    accept="image/*" onChange={onSelectFile} />
            </div>
        </>
    )
}
